import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { LicenseHostMap } from '@portal-core/license-host-maps/models/license-host-map.model';
import { LicenseHostMapFrontDoorState } from '@portal-core/license-host-maps/models/license-host-map-front-door-state.model';
import { LicenseHostMapSslCertificate } from '@portal-core/license-host-maps/models/license-host-map-ssl-certificate.model';
import { LicenseHostMapUpdateRobotsExclusion } from '@portal-core/license-host-maps/models/license-host-map-update-robots-exclusion.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable, map } from 'rxjs';

@Service()
export class LicenseHostMapsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getLicenseHostMap$(licenseHostMapId: number): Observable<LicenseHostMap> {
    return this.http.get<LicenseHostMap>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}`);
  }

  @SharePendingRequest()
  getCNameValidation$(licenseId: number, domain: string, legacyBinding: boolean, licenseHostMapId?: number): Observable<MadCloudResult<void>> {
    const licenseHostMapParam = licenseHostMapId !== undefined ? `&licenseHostMapId=${licenseHostMapId}` : '';
    return this.http.get<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseId}/validateCName?domain=${domain}&legacyBinding=${legacyBinding}${licenseHostMapParam}`);
  }

  @SharePendingRequest()
  getFrontDoorEndpointDomain$(domain: string): Observable<string> {
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/endpointDomain?domain=${domain}`);
  }

  @SharePendingRequest()
  getLicenseHostMapFrontDoorState$(licenseHostMapId: number): Observable<LicenseHostMapFrontDoorState> {
    return this.http.get<LicenseHostMapFrontDoorState>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/frontDoorState`);
  }

  @SharePendingRequest()
  getLicenseHostMapFrontDoorStates$(licenseHostMapIds: number[]): Observable<LicenseHostMapFrontDoorState[]> {
    return this.http.post<LicenseHostMapFrontDoorState[]>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/frontDoorStates`, licenseHostMapIds);
  }

  @SharePendingRequest()
  getLicenseHostMapSslCertificate$(licenseHostMapId: number): Observable<LicenseHostMapSslCertificate> {
    return this.http.get<LicenseHostMapSslCertificate>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/sslCertificate`);
  }

  @SharePendingRequest()
  getLicenseHostMapDomainHealth$(licenseHostMapId: number): Observable<MadCloudResult<void>> {
    return this.http.get<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/domainHealth`);
  }

  regenerateValidationToken$(licenseHostMapId: number): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/regenerateValidationToken`, null);
  }

  @SharePendingRequest()
  getDefaultRobotsText$(licenseHostMapId: number): Observable<string> {
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/DefaultRobotsText`);
  }

  addLicenseHostMap$(licenseId: number, licenseHostMap: LicenseHostMap, email: string, password: string): Observable<LicenseHostMap> {
    const emailParam = email ? `?email=${encodeURIComponent(email)}` : '';
    const passwordParam = email && password ? `&password=${encodeURIComponent(password)}` : '';

    return this.http.post<LicenseHostMap>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseId}/addLicenseHostMap${emailParam}${passwordParam}`, licenseHostMap);
  }

  updateLicenseHostMap$(licenseId: number, licenseHostMap: LicenseHostMap, email: string, password: string): Observable<LicenseHostMap> {
    const emailParam = email ? `?email=${encodeURIComponent(email)}` : '';
    const passwordParam = email && password ? `&password=${encodeURIComponent(password)}` : '';

    return this.http.put<MadCloudResult<LicenseHostMap>>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseId}/licenseHostMapUpdate${emailParam}${passwordParam}`, licenseHostMap).pipe(
      map(result => result.Result)
    );
  }

  deleteLicenseHostMap$(licenseId: number, hostMapId: number): Observable<MadCloudResult<void>> {
    return this.http.delete<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseId}/deleteLicenseHostMap?id=${hostMapId}`);
  }

  setLicenseHostMapDefaultSite$(licenseHostMapId: number, defaultSiteId: number): Observable<void> {
    if (typeof defaultSiteId === 'number') {
      return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/setDefaultSite?defaultSiteId=${defaultSiteId}`, null);
    } else {
      return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/setDefaultSite`, null);
    }
  }

  setLicenseHostMapRobotsExclusion$(licenseHostMapId: number, updateModel: LicenseHostMapUpdateRobotsExclusion): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/LicenseHostMap/${licenseHostMapId}/setRobotsExclusion`, updateModel);
  }
}
